import React, { useState, useEffect, useCallback } from 'react';
import {
  Video,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Play,
  X,
  Save,
  Loader,
  Upload,
  Youtube,
  Image
} from 'lucide-react';
import tutorialsService from '../services/tutorialsService';
import { useToast } from '../context/ToastContext';
import './TutorialsPage.css';

const TutorialsPage = () => {
  const toast = useToast();
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [formData, setFormData] = useState({
    titleAr: '',
    titleEn: '',
    descriptionAr: '',
    descriptionEn: '',
    videoType: 'youtube',
    youtubeUrl: '',
    category: 'getting_started',
    order: 0,
    isPublished: true,
    duration: 0
  });
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const categories = [
    { value: 'getting_started', label: 'البداية' },
    { value: 'offers', label: 'العروض' },
    { value: 'requests', label: 'الطلبات' },
    { value: 'network', label: 'الشبكة' },
    { value: 'catalog', label: 'الكاتالوج' },
    { value: 'settings', label: 'الإعدادات' },
    { value: 'other', label: 'أخرى' }
  ];

  const fetchTutorials = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterActive !== 'all') params.isPublished = filterActive;
      
      const response = await tutorialsService.getAll(params);
      if (response.success) {
        setTutorials(response.data.tutorials || []);
      }
    } catch (err) {
      toast.error(err.message || 'حدث خطأ أثناء تحميل الفيديوهات التعليمية');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterActive, toast]);

  useEffect(() => {
    fetchTutorials();
  }, [fetchTutorials]);

  const filteredTutorials = tutorials.filter(tutorial => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!tutorial.titleAr?.toLowerCase().includes(query) &&
          !tutorial.titleEn?.toLowerCase().includes(query) &&
          !tutorial.descriptionAr?.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });

  const handleOpenModal = (mode, tutorial = null) => {
    setModalMode(mode);
    setSelectedTutorial(tutorial);
    setVideoFile(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    
    if (tutorial) {
      setFormData({
        titleAr: tutorial.titleAr || '',
        titleEn: tutorial.titleEn || '',
        descriptionAr: tutorial.descriptionAr || '',
        descriptionEn: tutorial.descriptionEn || '',
        videoType: tutorial.videoType || 'youtube',
        youtubeUrl: tutorial.youtubeUrl || '',
        category: tutorial.category || 'getting_started',
        order: tutorial.order || 0,
        isPublished: tutorial.isPublished !== false,
        duration: tutorial.duration || 0
      });
      if (tutorial.thumbnail) {
        setThumbnailPreview(tutorial.thumbnail);
      }
    } else {
      setFormData({
        titleAr: '',
        titleEn: '',
        descriptionAr: '',
        descriptionEn: '',
        videoType: 'youtube',
        youtubeUrl: '',
        category: 'getting_started',
        order: tutorials.length,
        isPublished: true,
        duration: 0
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTutorial(null);
    setVideoFile(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!formData.titleAr || !formData.titleEn) {
      toast.warning('يرجى ملء العنوان بالعربي والإنجليزي');
      return;
    }

    if (formData.videoType === 'youtube' && !formData.youtubeUrl) {
      toast.warning('يرجى إدخال رابط يوتيوب');
      return;
    }

    if (formData.videoType === 'upload' && !videoFile && modalMode === 'create') {
      toast.warning('يرجى رفع ملف الفيديو');
      return;
    }

    setSaving(true);
    try {
      const submitData = new FormData();
      submitData.append('titleAr', formData.titleAr);
      submitData.append('titleEn', formData.titleEn);
      submitData.append('descriptionAr', formData.descriptionAr);
      submitData.append('descriptionEn', formData.descriptionEn);
      submitData.append('videoType', formData.videoType);
      submitData.append('category', formData.category);
      submitData.append('order', formData.order);
      submitData.append('isPublished', formData.isPublished);
      submitData.append('duration', formData.duration);

      if (formData.videoType === 'youtube') {
        submitData.append('youtubeUrl', formData.youtubeUrl);
      }

      if (videoFile) {
        submitData.append('video', videoFile);
      }

      if (thumbnailFile) {
        submitData.append('thumbnail', thumbnailFile);
      }

      if (modalMode === 'create') {
        const response = await tutorialsService.create(submitData);
        if (response.success) {
          fetchTutorials();
          handleCloseModal();
        }
      } else {
        const response = await tutorialsService.update(selectedTutorial._id, submitData);
        if (response.success) {
          fetchTutorials();
          handleCloseModal();
        }
      }
    } catch (err) {
      toast.error(err.message || 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (tutorial) => {
    try {
      const submitData = new FormData();
      submitData.append('isPublished', !tutorial.isPublished);
      await tutorialsService.update(tutorial._id, submitData);
      fetchTutorials();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (tutorial) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${tutorial.titleAr}"؟`)) return;
    
    try {
      await tutorialsService.delete(tutorial._id);
      fetchTutorials();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
    return match ? match[1] : null;
  };

  return (
    <div className="tutorials-page">
      <div className="page-header">
        <div className="header-title">
          <Video size={28} />
          <div>
            <h1>إدارة الفيديوهات التعليمية</h1>
            <p>إضافة وتعديل الفيديوهات التوعوية للمستخدمين</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => handleOpenModal('create')}>
          <Plus size={18} />
          إضافة فيديو
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="بحث في الفيديوهات..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="all">جميع التصنيفات</option>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
        >
          <option value="all">جميع الحالات</option>
          <option value="true">منشور</option>
          <option value="false">غير منشور</option>
        </select>
      </div>

      {/* Tutorials Grid */}
      {loading ? (
        <div className="loading-state">
          <Loader className="spinner" size={32} />
          <p>جاري التحميل...</p>
        </div>
      ) : filteredTutorials.length === 0 ? (
        <div className="empty-state">
          <Video size={64} />
          <h3>لا توجد فيديوهات</h3>
          <p>ابدأ بإضافة فيديوهات تعليمية للمستخدمين</p>
          <button className="btn btn-primary" onClick={() => handleOpenModal('create')}>
            <Plus size={18} />
            إضافة فيديو
          </button>
        </div>
      ) : (
        <div className="tutorials-grid">
          {filteredTutorials.map(tutorial => (
            <div key={tutorial._id} className={`tutorial-card ${!tutorial.isPublished ? 'inactive' : ''}`}>
              <div className="tutorial-thumbnail">
                {tutorial.thumbnail ? (
                  <img src={tutorial.thumbnail} alt={tutorial.titleAr} />
                ) : tutorial.videoType === 'youtube' && getYouTubeId(tutorial.youtubeUrl) ? (
                  <img 
                    src={`https://img.youtube.com/vi/${getYouTubeId(tutorial.youtubeUrl)}/mqdefault.jpg`}
                    alt={tutorial.titleAr}
                  />
                ) : (
                  <div className="no-thumbnail">
                    <Play size={40} />
                  </div>
                )}
                <div className="thumbnail-overlay">
                  <Play size={48} />
                </div>
                <div className="video-type-badge">
                  {tutorial.videoType === 'upload' ? (
                    <><Upload size={12} /> مرفوع</>
                  ) : (
                    <><Youtube size={12} /> يوتيوب</>
                  )}
                </div>
                {!tutorial.isPublished && (
                  <span className="inactive-badge">غير منشور</span>
                )}
              </div>
              <div className="tutorial-content">
                <h3>{tutorial.titleAr}</h3>
                <p>{tutorial.descriptionAr}</p>
                <div className="tutorial-meta">
                  <span className="category-badge">
                    {categories.find(c => c.value === tutorial.category)?.label || tutorial.category}
                  </span>
                  <span className="order-badge">الترتيب: {tutorial.order}</span>
                </div>
              </div>
              <div className="tutorial-actions">
                <button 
                  className="action-btn"
                  onClick={() => window.open(tutorial.videoUrl || tutorial.youtubeUrl, '_blank')}
                  title="فتح الفيديو"
                >
                  <ExternalLink size={16} />
                </button>
                <button 
                  className="action-btn"
                  onClick={() => handleToggleActive(tutorial)}
                  title={tutorial.isPublished ? 'إخفاء' : 'نشر'}
                >
                  {tutorial.isPublished ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button 
                  className="action-btn"
                  onClick={() => handleOpenModal('edit', tutorial)}
                  title="تعديل"
                >
                  <Edit size={16} />
                </button>
                <button 
                  className="action-btn delete"
                  onClick={() => handleDelete(tutorial)}
                  title="حذف"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Video size={24} />
                {modalMode === 'create' ? 'إضافة فيديو جديد' : 'تعديل الفيديو'}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {/* Titles */}
              <div className="form-row">
                <div className="form-group">
                  <label>العنوان بالعربي *</label>
                  <input
                    type="text"
                    value={formData.titleAr}
                    onChange={(e) => setFormData({ ...formData, titleAr: e.target.value })}
                    placeholder="عنوان الفيديو بالعربي"
                  />
                </div>
                <div className="form-group">
                  <label>العنوان بالإنجليزي *</label>
                  <input
                    type="text"
                    value={formData.titleEn}
                    onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                    placeholder="Video title in English"
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div className="form-row">
                <div className="form-group">
                  <label>الوصف بالعربي</label>
                  <textarea
                    value={formData.descriptionAr}
                    onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                    placeholder="وصف مختصر للفيديو"
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>الوصف بالإنجليزي</label>
                  <textarea
                    value={formData.descriptionEn}
                    onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                    placeholder="Short video description"
                    rows={3}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Video Type Selection */}
              <div className="form-group">
                <label>نوع الفيديو *</label>
                <div className="video-type-selector">
                  <button
                    type="button"
                    className={`type-btn ${formData.videoType === 'youtube' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, videoType: 'youtube' })}
                  >
                    <Youtube size={20} />
                    رابط يوتيوب
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${formData.videoType === 'upload' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, videoType: 'upload' })}
                  >
                    <Upload size={20} />
                    رفع فيديو
                  </button>
                </div>
              </div>

              {/* YouTube URL or Upload */}
              {formData.videoType === 'youtube' ? (
                <div className="form-group">
                  <label>رابط يوتيوب *</label>
                  <input
                    type="url"
                    value={formData.youtubeUrl}
                    onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    dir="ltr"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>ملف الفيديو {modalMode === 'create' ? '*' : ''}</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoFileChange}
                      id="video-upload"
                    />
                    <label htmlFor="video-upload" className="file-upload-label">
                      <Upload size={32} />
                      <span>{videoFile ? videoFile.name : 'اختر ملف فيديو أو اسحبه هنا'}</span>
                      <small>MP4, WebM, MOV - حتى 50MB</small>
                    </label>
                  </div>
                  {selectedTutorial?.videoUrl && !videoFile && (
                    <p className="current-file-note">الفيديو الحالي سيبقى إذا لم ترفع فيديو جديد</p>
                  )}
                </div>
              )}

              {/* Thumbnail */}
              <div className="form-group">
                <label>صورة الغلاف (اختياري)</label>
                <div className="thumbnail-upload-area">
                  {thumbnailPreview ? (
                    <div className="thumbnail-preview">
                      <img src={thumbnailPreview} alt="Thumbnail preview" />
                      <button 
                        type="button" 
                        className="remove-thumbnail"
                        onClick={() => {
                          setThumbnailFile(null);
                          setThumbnailPreview(null);
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailChange}
                        id="thumbnail-upload"
                      />
                      <label htmlFor="thumbnail-upload" className="file-upload-label small">
                        <Image size={24} />
                        <span>اختر صورة الغلاف</span>
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Category, Order, Duration */}
              <div className="form-row three-cols">
                <div className="form-group">
                  <label>التصنيف</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>الترتيب</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                    min={0}
                  />
                </div>
                <div className="form-group">
                  <label>المدة (ثواني)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                    min={0}
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  />
                  منشور (ظاهر للمستخدمين)
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <Loader className="spinner" size={18} /> : <Save size={18} />}
                {modalMode === 'create' ? 'إضافة' : 'حفظ التعديلات'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialsPage;
